import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReusableTable from './ReusableTable';
import en from '../../copy/en';

export default function SavingsInvestmentsTable({ monthData, currency, onSave }) {
  const [savingsData, setSavingsData] = useState(() => {
    // Initialize with real data if available, otherwise empty default
    if (monthData?.savings && monthData.savings.length > 0) {
      return monthData.savings;
    }
    return [{ category: '', amount: 0, id: 'default-savings-row-0' }];
  });
  const isUserOperatingRef = useRef(false);
  const previousMonthDataRef = useRef(null); // Initialize to null, not monthData
  const lastSaveDataRef = useRef(null);

  // Helper function to compare data arrays
  const areDataArraysEqual = useCallback((arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return item1.id === item2.id && 
             item1.category === item2.category && 
             item1.amount === item2.amount;
    });
  }, []);

  // Load data from monthData whenever it changes
  useEffect(() => {
    // Skip if user is actively editing
    if (isUserOperatingRef.current) return;

    // Skip if monthData hasn't changed
    if (monthData === previousMonthDataRef.current) return;

    const hasSavingsData = monthData?.savings && monthData.savings.length > 0;
    const isDifferentFromCurrent = !areDataArraysEqual(monthData?.savings, savingsData);

    if (hasSavingsData && isDifferentFromCurrent) {
      // Update with real data from monthData
      setSavingsData(monthData.savings);
    } else if (!hasSavingsData && savingsData.length === 0) {
      // No data and no current data - set default row
      setSavingsData([{ category: '', amount: 0, id: 'default-savings-row-0' }]);
    }

    // Update the previous monthData reference
    previousMonthDataRef.current = monthData;
  }, [monthData, savingsData, areDataArraysEqual]);

  const handleDataChange = (newData) => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;
    
    setSavingsData(newData);
    
    // Calculate total savings
    const totalSavings = newData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Save to backend
    if (onSave) {
      onSave({ 
        savings: newData,
        totalSavings: totalSavings
      });
    }
    
    // Update the last save data reference
    lastSaveDataRef.current = newData;
    
    // Reset user operating flag after a longer delay to ensure save completes
    setTimeout(() => {
      isUserOperatingRef.current = false;
    }, 1000);
  };

  // Calculate budget goal and determine label and amount
  const calculateBudgetStatus = () => {
    const totalIncome = monthData?.income ? monthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;

    const savingsGoalPercentage = monthData?.budgetGoals?.savingsPercentage || 20;
    const savingsGoalAmount = totalIncome * (savingsGoalPercentage / 100);

    const actualSavingsAmount = savingsData.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    if (actualSavingsAmount > savingsGoalAmount) {
      return {
        label: "Overspent",
        amount: actualSavingsAmount - savingsGoalAmount
      };
    } else if (actualSavingsAmount === savingsGoalAmount) {
      return {
        label: "Total",
        amount: actualSavingsAmount
      };
    } else {
      return {
        label: "Left to assign",
        amount: savingsGoalAmount - actualSavingsAmount
      };
    }
  };

  const budgetStatus = calculateBudgetStatus();

  const tooltipContent = en.finance.tooltips.savings.description;

  return (
    <ReusableTable
      title="Savings & Investments"
      data={savingsData}
      onDataChange={handleDataChange}
      addRowLabel="Add savings/investment"
      currency={currency}
      showTotal={true}
      totalLabel={budgetStatus.label}
      customTotalAmount={budgetStatus.amount}
      tooltipContent={tooltipContent}
    />
  );
}
